/**
 * Theme store — single source of truth for user theme preferences.
 *
 * localStorage-backed, framework-agnostic. `applyResolvedTheme()` writes
 * data-theme + data-contrast + data-accent + data-motion to the root
 * element; React subscribes via useSyncExternalStore in ThemeProvider.
 */

import {
  ACCENTS,
  BG_IMAGE_FITS,
  DEFAULT_CUSTOM_ACCENT_HEX,
  DEFAULT_PREFS,
  GRADIENT_HEIGHT_MAX,
  GRADIENT_HEIGHT_MIN,
  MODES,
  STORAGE_KEY,
  TILE_SIZE_MAX,
  TILE_SIZE_MIN,
  type AccentKey,
  type AutoMode,
  type BgImageEntry,
  type BgImageFit,
  type ContrastLevel,
  type MotionPref,
  type ThemeMode,
  type ThemePrefs,
} from './theme-types.js';

const HEX6 = /^#[0-9a-fA-F]{6}$/;

export function hexToRgbTriplet(hex: string): string {
  const m = HEX6.exec(hex);
  if (m === null) return DEFAULT_PREFS.customAccentHex.slice(1);
  const n = parseInt(hex.slice(1), 16);
  return `${(n >> 16) & 0xff} ${(n >> 8) & 0xff} ${n & 0xff}`;
}

let current: ThemePrefs = loadFromStorage();
const listeners = new Set<() => void>();

function loadFromStorage(): ThemePrefs {
  if (typeof localStorage === 'undefined') return { ...DEFAULT_PREFS };
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw === null) return { ...DEFAULT_PREFS };
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    // Legacy shape (pre-library): single `bgImage` data URL. Migrate it to
    // the first entry of `bgImages` and mark it active.
    let bgImages: BgImageEntry[] = isBgImageList(parsed.bgImages)
      ? parsed.bgImages
      : DEFAULT_PREFS.bgImages;
    let activeBgImageId: string | null = isActiveBgImageId(
      parsed.activeBgImageId,
    )
      ? parsed.activeBgImageId
      : DEFAULT_PREFS.activeBgImageId;
    if (
      bgImages.length === 0 &&
      typeof parsed.bgImage === 'string' &&
      parsed.bgImage.length > 0
    ) {
      const migrated: BgImageEntry = {
        id: newBgImageId(),
        dataUrl: parsed.bgImage,
        name: 'Background',
        createdAt: Date.now(),
      };
      bgImages = [migrated];
      activeBgImageId = migrated.id;
    }
    // Reconcile: the active id must exist in the library, else clear it.
    if (
      activeBgImageId !== null &&
      !bgImages.some((b) => b.id === activeBgImageId)
    ) {
      activeBgImageId = null;
    }
    return {
      mode: isMode(parsed.mode) ? parsed.mode : DEFAULT_PREFS.mode,
      contrast: isContrast(parsed.contrast)
        ? parsed.contrast
        : DEFAULT_PREFS.contrast,
      accent: isAccent(parsed.accent) ? parsed.accent : DEFAULT_PREFS.accent,
      autoMode: isAutoMode(parsed.autoMode)
        ? parsed.autoMode
        : DEFAULT_PREFS.autoMode,
      motion: isMotion(parsed.motion) ? parsed.motion : DEFAULT_PREFS.motion,
      gradientHeight: isGradientHeight(parsed.gradientHeight)
        ? parsed.gradientHeight
        : DEFAULT_PREFS.gradientHeight,
      bgImages,
      activeBgImageId,
      bgImageFit: isBgImageFit(parsed.bgImageFit)
        ? parsed.bgImageFit
        : DEFAULT_PREFS.bgImageFit,
      bgTileSize: isTileSize(parsed.bgTileSize)
        ? parsed.bgTileSize
        : DEFAULT_PREFS.bgTileSize,
      customAccentHex: isHex6(parsed.customAccentHex)
        ? parsed.customAccentHex
        : DEFAULT_CUSTOM_ACCENT_HEX,
    };
  } catch {
    return { ...DEFAULT_PREFS };
  }
}

export function newBgImageId(): string {
  if (
    typeof crypto !== 'undefined' &&
    typeof crypto.randomUUID === 'function'
  ) {
    return crypto.randomUUID();
  }
  return `bg-${Date.now()}-${Math.floor(Math.random() * 1e6).toString(36)}`;
}

function persist(next: ThemePrefs): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    /* quota / private-mode — ignore */
  }
}

export function getPrefs(): ThemePrefs {
  return current;
}

export function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function emit(): void {
  for (const l of listeners) l();
}

export function updatePrefs(patch: Partial<ThemePrefs>): void {
  current = { ...current, ...patch };
  persist(current);
  applyResolvedTheme();
  emit();
}

/**
 * Resolve what ThemeMode should actually be rendered right now.
 *
 * Manual → prefs.mode. System → OS prefers-color-scheme. Contrast is
 * orthogonal and always honours `prefs.contrast`.
 */
export function resolveMode(prefs: ThemePrefs = current): ThemeMode {
  if (prefs.autoMode === 'manual') return prefs.mode;
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function')
    return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches
    ? 'dark'
    : 'light';
}

export function resolveMotion(prefs: ThemePrefs = current): 'full' | 'reduced' {
  if (prefs.motion === 'full') return 'full';
  if (prefs.motion === 'reduced') return 'reduced';
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function')
    return 'full';
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches
    ? 'reduced'
    : 'full';
}

/**
 * Apply resolved attributes to `document.documentElement`.
 *
 * Call before first React render in `boot.tsx` so the initial paint is
 * already themed (no flash), and again whenever prefs or the system
 * preference changes.
 */
export function applyResolvedTheme(): void {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  root.setAttribute('data-theme', resolveMode(current));
  root.setAttribute('data-contrast', current.contrast);
  root.setAttribute('data-accent', current.accent);
  if (current.accent === 'custom') {
    root.style.setProperty(
      '--accent-rgb',
      hexToRgbTriplet(current.customAccentHex),
    );
  } else {
    root.style.removeProperty('--accent-rgb');
  }
  root.setAttribute('data-motion', resolveMotion(current));
  // Bottom gradient: stop position is where the tint begins. gradientHeight
  // is 0-50 (% of viewport consumed by the tint), so stop = 100% - height.
  // When height === 0 the gradient is fully suppressed via [data-bg-grad].
  const stop = Math.max(0, Math.min(100, 100 - current.gradientHeight));
  root.style.setProperty('--body-grad-stop', `${stop}%`);
  if (current.gradientHeight === 0) {
    root.setAttribute('data-bg-grad', 'off');
  } else {
    root.removeAttribute('data-bg-grad');
  }
  // Background image: write the active image (if any) as `--body-bg-image`
  // and flip body rules via `[data-has-bgimg]`. Tile size is published as
  // a vw-% CSS var so the tile branch can size its background-size.
  const active =
    current.bgImages.find((b) => b.id === current.activeBgImageId) ?? null;
  if (active !== null) {
    root.style.setProperty('--body-bg-image', `url("${active.dataUrl}")`);
    root.style.setProperty('--body-bg-tile-size', `${current.bgTileSize}vw`);
    root.setAttribute('data-has-bgimg', 'true');
    root.setAttribute('data-bg-fit', current.bgImageFit);
  } else {
    root.style.removeProperty('--body-bg-image');
    root.style.removeProperty('--body-bg-tile-size');
    root.removeAttribute('data-has-bgimg');
    root.removeAttribute('data-bg-fit');
  }
}

/** Attach media-query listeners. Returns a cleanup function. */
export function watchSystem(): () => void {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function')
    return () => {};
  const colorMql = window.matchMedia('(prefers-color-scheme: dark)');
  const motionMql = window.matchMedia('(prefers-reduced-motion: reduce)');
  const handler = (): void => {
    applyResolvedTheme();
    emit();
  };
  colorMql.addEventListener('change', handler);
  motionMql.addEventListener('change', handler);
  return () => {
    colorMql.removeEventListener('change', handler);
    motionMql.removeEventListener('change', handler);
  };
}

/* ----- type guards ----- */

function isMode(v: unknown): v is ThemeMode {
  return typeof v === 'string' && (MODES as readonly string[]).includes(v);
}
function isContrast(v: unknown): v is ContrastLevel {
  return v === 'normal' || v === 'high';
}
function isAccent(v: unknown): v is AccentKey {
  if (v === 'custom') return true;
  return typeof v === 'string' && ACCENTS.some((a) => a.key === v);
}
function isHex6(v: unknown): v is string {
  return typeof v === 'string' && HEX6.test(v);
}
function isAutoMode(v: unknown): v is AutoMode {
  return v === 'manual' || v === 'system';
}
function isMotion(v: unknown): v is MotionPref {
  return v === 'system' || v === 'full' || v === 'reduced';
}
function isGradientHeight(v: unknown): v is number {
  return (
    typeof v === 'number' &&
    Number.isFinite(v) &&
    v >= GRADIENT_HEIGHT_MIN &&
    v <= GRADIENT_HEIGHT_MAX
  );
}
function isBgImageEntry(v: unknown): v is BgImageEntry {
  if (v === null || typeof v !== 'object') return false;
  const r = v as Record<string, unknown>;
  return (
    typeof r.id === 'string' &&
    r.id.length > 0 &&
    typeof r.dataUrl === 'string' &&
    r.dataUrl.length > 0 &&
    typeof r.name === 'string' &&
    typeof r.createdAt === 'number' &&
    Number.isFinite(r.createdAt)
  );
}
function isBgImageList(v: unknown): v is BgImageEntry[] {
  return Array.isArray(v) && v.every(isBgImageEntry);
}
function isActiveBgImageId(v: unknown): v is string | null {
  return v === null || (typeof v === 'string' && v.length > 0);
}
function isBgImageFit(v: unknown): v is BgImageFit {
  return typeof v === 'string' && BG_IMAGE_FITS.some((f) => f.key === v);
}
function isTileSize(v: unknown): v is number {
  return (
    typeof v === 'number' &&
    Number.isFinite(v) &&
    v >= TILE_SIZE_MIN &&
    v <= TILE_SIZE_MAX
  );
}

export const __test__ = { loadFromStorage };
